import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TruckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { TimelineModal } from '../../components/transport/TimelineModal';

type CarrierInvitation = {
  carrier_id: number;
  carrier_name: string;
  recipient_emails: {
    main_email?: string;
    forwarder_emails?: Array<{name: string; email: string}>;
    driver_emails?: Array<{name: string; email: string}>;
  };
  status?: string;
  offer_price?: string;
  offer_currency?: string;
  vehicle_type?: string;
  delivery_eta?: string;
  notes?: string;
  responded_at?: string;
  selected?: boolean;
};

type OrderInTransport = {
  order_id: number;
  order_number: string;
  ref_name?: string;
  order_date?: string;
  responsible?: string;
  estimated_delivery_date?: string;
  product_short_description?: string;
};

type Message = {
  id: number;
  transport_id?: number;
  carrier_id?: number;
  direction: 'outgoing' | 'incoming';
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  body?: string;
  sent_at?: string;
  created_at?: string;
};

type Transport = {
  id: number;
  reference?: string;
  status: string;
  orders?: OrderInTransport[];
  carrier_invitations?: CarrierInvitation[];
  packages?: any[];
  created_at?: string;
  updated_at?: string;
};

const STATUS_LABELS: Record<string, string> = {
  delivered: 'Dostarczono',
  closed: 'Zamknięty'
};

export default function TransportArchive() {
  const { t } = useTranslation();
  const [archivedTransports, setArchivedTransports] = useState<Transport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [messagesMap, setMessagesMap] = useState<Record<number, Message[]>>({});
  const [timelineModal, setTimelineModal] = useState<{ open: boolean; transport: Transport | null }>({
    open: false,
    transport: null
  });

  const loadTransports = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/transport');
      const allTransports = resp.data.data || resp.data || [];

      // Filter only delivered transports
      const archived = allTransports.filter((t: Transport) => t.status === 'delivered');
      setArchivedTransports(archived);
    } catch (e: any) {
      toast.error(e?.message || 'Błąd ładowania archiwum');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (transportId: number) => {
    try {
      const resp = await api.get(`/transport/${transportId}/messages`);
      const msgs = resp.data.data || resp.data || [];
      setMessagesMap(prev => ({ ...prev, [transportId]: msgs }));
    } catch (e: any) {
      console.error('Failed to load messages:', e);
      toast.error('Nie udało się załadować wiadomości');
    }
  };

  const deleteFromArchive = async (transportId: number) => {
    if (!confirm('Czy na pewno chcesz TRWALE USUNĄĆ to zlecenie z archiwum?\n\nTa operacja usunie:\n- Zlecenie transportowe\n- Wszystkie wiadomości\n- Wszystkie zamówienia przypisane do tego zlecenia\n\nTej operacji nie można cofnąć!')) {
      return;
    }

    try {
      await api.delete(`/transport/${transportId}/archive`);
      toast.success('Zlecenie zostało trwale usunięte z archiwum wraz z powiązanymi zamówieniami i wiadomościami');
      await loadTransports();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Nie udało się usunąć zlecenia z archiwum');
    }
  };

  useEffect(() => {
    loadTransports();
  }, []);

  if (loading) return <div className="p-6">{t('common.loading', 'Ładowanie...')}</div>;

  // Filter by search query
  const filteredTransports = archivedTransports.filter(transport => {
    const searchTerm = searchQuery.toLowerCase();

    // Search in ID
    if (transport.id.toString().includes(searchTerm)) return true;

    // Search in reference
    if (transport.reference?.toLowerCase().includes(searchTerm)) return true;

    // Search in orders
    if (transport.orders?.some(order =>
      order.order_number.toLowerCase().includes(searchTerm) ||
      order.ref_name?.toLowerCase().includes(searchTerm)
    )) return true;

    // Search in carriers
    if (transport.carrier_invitations?.some(inv =>
      inv.carrier_name.toLowerCase().includes(searchTerm)
    )) return true;

    return false;
  });

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TruckIcon className="w-6 h-6 text-green-600" />
                Archiwum zleceń transportowych ({filteredTransports.length})
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">Zlecenia zakończone i dostarczone</p>
            </div>
          </div>

          {/* Search bar */}
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Szukaj zlecenia po ID, zamówieniu, przewoźniku..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID Zlecenia
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zamówienia
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Przewoźnicy
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paczki
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransports.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      {searchQuery ? 'Nie znaleziono zleceń pasujących do wyszukiwania' : 'Brak archiwalnych zleceń'}
                    </td>
                  </tr>
                ) : (
                  filteredTransports.map(transport => {
                    const respondedInvitations = (transport.carrier_invitations || []).filter(
                      inv => inv.status === 'responded'
                    );

                    return (
                      <tr key={transport.id} className="hover:bg-gray-50">
                        {/* ID */}
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            <TruckIcon className="w-4 h-4 text-green-600" />
                            #{transport.id}
                          </div>
                        </td>

                        {/* Zamówienia */}
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {transport.orders && transport.orders.length > 0 ? (
                            <div className="space-y-1">
                              {transport.orders.slice(0, 2).map((order: OrderInTransport) => (
                                <div key={order.order_id} className="truncate max-w-xs">
                                  {order.order_number} {order.ref_name ? `(${order.ref_name})` : ''}
                                </div>
                              ))}
                              {transport.orders.length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{transport.orders.length - 2} więcej
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>

                        {/* Przewoźnicy */}
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {transport.carrier_invitations && transport.carrier_invitations.length > 0 ? (
                            <div className="space-y-1">
                              {transport.carrier_invitations.slice(0, 2).map((inv: CarrierInvitation) => (
                                <div key={inv.carrier_id} className="flex items-center gap-1">
                                  {inv.carrier_name}
                                  {inv.status === 'responded' && (
                                    <CheckCircleIcon className="w-3 h-3 text-green-600" />
                                  )}
                                </div>
                              ))}
                              {transport.carrier_invitations.length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{transport.carrier_invitations.length - 2} więcej
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>

                        {/* Paczki */}
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {transport.packages && transport.packages.length > 0 ? (
                            <span>{transport.packages.length} szt.</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                            {STATUS_LABELS[transport.status] || transport.status}
                          </span>
                        </td>

                        {/* Akcje */}
                        <td className="px-4 py-3 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={async () => {
                                await loadMessages(transport.id);
                                setTimelineModal({ open: true, transport });
                              }}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Szczegóły
                              {respondedInvitations.length > 0 && (
                                <span className="ml-1.5 px-2 py-0.5 bg-orange-500 text-white rounded-full text-xs">
                                  {respondedInvitations.length}
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() => deleteFromArchive(transport.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Usuń
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Timeline Modal */}
      <TimelineModal
        isOpen={timelineModal.open}
        onClose={() => setTimelineModal({ open: false, transport: null })}
        transport={timelineModal.transport}
        messages={timelineModal.transport ? messagesMap[timelineModal.transport.id] || [] : []}
        onRefresh={async () => {
          await loadTransports();
          if (timelineModal.transport) {
            await loadMessages(timelineModal.transport.id);
          }
        }}
      />
    </div>
  );
}
